import React, { useCallback } from 'react'

import { Expander, ExpanderTitleRendererParams } from '@amzn/stencil-react-components/expander'
import { Col, Hr, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { H3, Text } from '@amzn/stencil-react-components/text'

import { ClickableH3 } from 'src/components/ExpanderHeadings'

interface OutputScoresProps {
    outputScoreVariables: string[]
    /** Set where the module can hold unsaved edits, to say this list may lag behind them. */
    showSaveHint?: boolean
    /** Set when the module is holding unsaved edits, so this list is known to be out of date. */
    hasUnsavedChanges?: boolean
}

/**
 * Nothing is reported both when a version has no scoring and when its draft has not validated yet, so
 * there is nothing worth showing either way.
 */
const hasNothingToShow = (outputScoreVariables: string[]) => outputScoreVariables.length === 0

const OutputScoreVariables = ({
    outputScoreVariables,
    showSaveHint,
    hasUnsavedChanges,
}: OutputScoresProps) => (
    <>
        <Text fontSize='T200' color='neutral70'>
            The score variables this module version produces. Exact match with what workflow builder
            receives.
            {showSaveHint && (
                <>
                    {' '}
                    This reflects the <strong>last saved</strong> version — save the module to bring
                    it up to date with any changes.
                </>
            )}
        </Text>
        <Spacer height={'S200'} />
        {hasUnsavedChanges && (
            <>
                <MessageBanner type={MessageBannerType.Warning}>
                    This module has unsaved changes, so this list is out of date. Save the module to
                    see the output scores it produces now.
                </MessageBanner>
                <Spacer height={'S200'} />
            </>
        )}
        {/* boxed and monospaced to read as the literal identifiers they are, rather than as a
            continuation of the description above */}
        <View
            border={'1px solid #B9C0C8'}
            backgroundColor={'neutral05'}
            padding={{ top: 'S200', bottom: 'S200', left: 'S300', right: 'S300' }}
        >
            <ul data-test-id={'output-score-variables'}>
                {outputScoreVariables.map((variableName) => (
                    <li key={variableName}>
                        <Text font='monospace' fontSize='T200' color='neutral90'>
                            {variableName}
                        </Text>
                    </li>
                ))}
            </ul>
        </View>
    </>
)

/**
 * The output score variables a module version produces, shown inline below the composite scores in the
 * builder's Evaluation Scores section — read-only, so there is nothing to collapse away.
 *
 * Rides on its host being rendered: Evaluation Scores is hidden when a module has no composite scores
 * of its own, so a module producing output scores without them — an adaptive one, say — will not show
 * these there. Use {@link OutputScoresCard} wherever Evaluation Scores is absent.
 */
export const OutputScores = ({
    outputScoreVariables,
    showSaveHint,
    hasUnsavedChanges,
}: OutputScoresProps) => {
    if (hasNothingToShow(outputScoreVariables)) {
        return null
    }

    return (
        <Col dataTestId={'output-scores'}>
            <View width={'100%'} padding={{ top: 'S400', bottom: 'S300' }}>
                <Hr />
            </View>
            <H3 fontSize='T300' fontWeight='bold' color='neutral90'>
                Output
            </H3>
            <Spacer height={'S200'} />
            <OutputScoreVariables
                outputScoreVariables={outputScoreVariables}
                showSaveHint={showSaveHint}
                hasUnsavedChanges={hasUnsavedChanges}
            />
        </Col>
    )
}

/**
 * The same output score variables as a standalone card, styled to match the expandable page sections
 * the module viewer renders around it.
 */
export const OutputScoresCard = ({
    outputScoreVariables,
    showSaveHint,
    hasUnsavedChanges,
}: OutputScoresProps) => {
    const renderTitle = useCallback(
        ({ titleText, toggle }: ExpanderTitleRendererParams) => (
            <Row justifyContent='space-between' width='100%'>
                <ClickableH3 onClick={toggle}>{titleText}</ClickableH3>
            </Row>
        ),
        []
    )

    if (hasNothingToShow(outputScoreVariables)) {
        return null
    }

    return (
        <View backgroundColor='neutral0' padding='S300' dataTestId={'output-scores'}>
            <Expander titleText={'Output Scores'} renderTitle={renderTitle}>
                <OutputScoreVariables
                    outputScoreVariables={outputScoreVariables}
                    showSaveHint={showSaveHint}
                    hasUnsavedChanges={hasUnsavedChanges}
                />
            </Expander>
        </View>
    )
}
